import {
	Conversation,
	InShort,
	RelatedPages,
	type Turn,
} from "@marketing/components/answer";
import { Article } from "@marketing/components/article";
import {
	DetailGrid,
	marketingHead,
	PageIntro,
	Section,
	SplitRow,
	Steps,
} from "@marketing/components/blocks";
import {
	BranchVisual,
	InboxVisual,
	WhatsAppVisual,
} from "@marketing/components/visuals";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "What is a WhatsApp AI agent for grocery orders?",
		a: "An assistant that answers your store's WhatsApp Business number, understands what the shopper wants in their own words, sends matching products from your catalog as cards, builds the basket and sends a checkout. Hephon Commerce does this in Arabic and English for supermarkets and grocery retailers, and the order is placed only when the shopper confirms with a tap.",
	},
	{
		q: "Do I need the WhatsApp Business API?",
		a: "You need a WhatsApp Business number on Meta's Cloud API, the official WhatsApp Business Platform. We connect it for you, register its webhooks and keep the connection healthy. Meta bills the messages to you directly.",
	},
	{
		q: "Can shoppers pay inside WhatsApp?",
		a: "The assistant builds the basket and sends a checkout the shopper confirms with a tap. Payment follows the options you choose: cash or card on delivery, or a payment link from your platform.",
	},
	{
		q: "Does the WhatsApp chatbot speak Arabic?",
		a: "Yes. It answers in Arabic or English, in the language the shopper writes in, and follows your shoppers' dialect, such as Saudi or Gulf Arabic. Lebanese, Arabizi and French work too.",
	},
	{
		q: "Can shoppers send voice notes or call the number?",
		a: "Yes. A voice note is transcribed and answered like a typed message. Shoppers can also call your WhatsApp number and order by voice; the basket is read back and the caller confirms on the keypad. Whether WhatsApp calls are available depends on the country and on Meta's rules for business calling.",
	},
	{
		q: "What can it never sell on WhatsApp?",
		a: "Meta's commerce policy does not allow alcohol or tobacco on WhatsApp, so those products are removed on our server for every WhatsApp conversation, whatever your other settings.",
	},
	{
		q: "Can my team take over a WhatsApp chat?",
		a: "Yes. Every chat is in your inbox as it happens. When a shopper asks for a person, the chat is flagged and your team replies in the same WhatsApp thread. The hand-over can also be sent to your help desk.",
	},
	{
		q: "Can I send marketing broadcasts with it?",
		a: "No. Hephon Commerce answers shoppers who message you and sends updates about their orders. It is not a bulk messaging tool for campaigns.",
	},
	{
		q: "How is WhatsApp ordering priced?",
		a: "Pricing is a proposal per retailer and depends on your stores and the channels you use. WhatsApp messages are billed to you by Meta, at Meta's prices, on top of that.",
	},
];

const CHAT: Turn[] = [
	{
		from: "shopper",
		text: "السلام عليكم، توصلون لحي النرجس؟",
		en: "Hello, do you deliver to Al Narjis?",
	},
	{
		from: "assistant",
		text: "وعليكم السلام. أرسل لي موقعك عشان أعرف أقرب فرع لك.",
		en: "Hello. Send me your location so I can find the nearest branch.",
		note: "This store asks for the location first, so no price goes out before a branch is set.",
	},
	{
		from: "shopper",
		text: "(shares a location pin)",
	},
	{
		from: "assistant",
		text: "تمام، أقرب فرع يوصل لك. تبي توصيل ولا استلام من الفرع؟",
		en: "Great, the nearest branch delivers to you. Delivery or pickup?",
		note: "The pin picked the nearest branch. Delivery and pickup are two buttons.",
	},
	{
		from: "shopper",
		text: "(voice note) توصيل. أبي حليب طازج وبيض وخبز توست",
		en: "Delivery. I want fresh milk, eggs and toast bread.",
		note: "The voice note is transcribed and answered like a typed message.",
	},
	{
		from: "assistant",
		text: "هذي الخيارات المتوفرة في فرعك:",
		en: "Here is what your branch has:",
		note: "Product cards with the branch's prices, as a WhatsApp list the shopper taps.",
	},
	{
		from: "shopper",
		text: "ضيف الثاني من الحليب وكرتون بيض",
		en: "Add the second milk and a carton of eggs.",
	},
	{
		from: "assistant",
		text: "أضفت الحليب وكرتون البيض للسلة. تبي شي ثاني؟",
		en: "I added the milk and the carton of eggs to your basket. Anything else?",
		note: "“The second one” counts in the list it just sent.",
	},
	{
		from: "shopper",
		text: "لا، خلاص",
		en: "No, that's all.",
	},
	{
		from: "assistant",
		text: "هذي سلتك. اضغط إتمام الطلب عشان تراجعها وتأكدها.",
		en: "Here is your basket. Tap Checkout to review and confirm it.",
		note: "Checkout opens as a form inside WhatsApp. Nothing is ordered yet.",
	},
	{
		from: "shopper",
		text: "(taps Confirm order)",
	},
	{
		from: "assistant",
		text: "تم تأكيد طلبك. بنرسل لك تحديثات الطلب هنا.",
		en: "Your order is confirmed. We will send its updates here.",
		note: "Only the tap placed the order. Updates and a tracking page follow in the same chat.",
	},
];

const ORDER = [
	{
		title: "Product cards in the chat",
		text: "Answers come with products, prices and pictures, as WhatsApp lists and buttons the shopper taps.",
	},
	{
		title: "Photos and voice notes",
		text: "A shopper can send a photo of a product or a voice note in their dialect. It is understood like a typed message.",
	},
	{
		title: "Checkout with a tap",
		text: "The basket becomes a checkout the shopper reviews and confirms. Nothing is ordered on a typed yes.",
	},
	{
		title: "Order updates",
		text: "Confirmed, out for delivery, delivered: the shopper hears about their order in the same chat, with a tracking page.",
	},
	{
		title: "WhatsApp calls",
		text: "Shoppers can call your number and order by voice, in Arabic or English.",
	},
	{
		title: "Meta's rules, enforced",
		text: "Alcohol and tobacco never appear on WhatsApp, removed on our server before the assistant sees your catalog.",
	},
];

const SETUP = [
	{
		title: "Your number, verified",
		text: "Your own WhatsApp Business number and name, connected to Meta's Cloud API for you.",
	},
	{
		title: "Templates and commands",
		text: "Message templates for order updates and the words shoppers can send, such as “basket” or “help”, set per number.",
	},
	{
		title: "Every message logged",
		text: "Each message, webhook and call is logged, and open problems are raised before shoppers notice them.",
	},
	{
		title: "Meta bills you directly",
		text: "WhatsApp conversations are billed by Meta to your business account, at Meta's prices.",
	},
];

const STEPS = [
	{
		title: "Connect your catalog",
		text: "From Shopify, WooCommerce, a product feed or our API, with each branch's prices and stock. Every product is checked against WhatsApp's rules.",
	},
	{
		title: "Connect your number",
		text: "Your WhatsApp Business number on Meta's Cloud API, with its templates, commands and whether to ask for the location first.",
	},
	{
		title: "Test, then open it",
		text: "We run the WhatsApp tests on your own catalog and you try it on a test phone. Then share the link or QR code with shoppers.",
	},
];

const RELATED = [
	{ href: "/channels/voice", label: "Voice ordering" },
	{ href: "/channels/in-app", label: "In your app" },
	{ href: "/integrations", label: "Integrations" },
	{ href: "/saudi-arabia", label: "For Saudi Arabia" },
	{ href: "/uae", label: "For the UAE" },
	{ href: "/supermarkets", label: "For supermarkets" },
];

export const Route = createFileRoute("/_marketing/channels/whatsapp")({
	head: () =>
		marketingHead({
			title: "WhatsApp AI Agent for Grocery Orders | Hephon Commerce",
			description:
				"Let shoppers order on WhatsApp in Arabic or English. The AI agent sends product cards, builds the basket and a checkout they confirm, on your own number.",
			path: "/channels/whatsapp",
			breadcrumb: ["WhatsApp shopping"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: WhatsAppPage,
});

function WhatsAppPage() {
	return (
		<>
			<PageIntro
				kicker="Your store on WhatsApp, open all night."
				title="A WhatsApp AI agent that takes grocery orders"
				lead="Shoppers ask in their own words. The assistant answers with real products, builds the order and sends a checkout they confirm with a tap, on your own number."
			>
				<WhatsAppVisual />
			</PageIntro>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce is a WhatsApp AI agent for supermarkets and
					grocery retailers. It answers the retailer's own WhatsApp
					Business number in Arabic or English, sends products from
					the live catalog as cards with the shopper's branch prices,
					builds the basket and sends a checkout.
				</p>
				<p>
					The order is placed only when the shopper confirms with a
					tap. Shoppers can type, send voice notes or call, and a
					person on the retailer's team can take over any chat.
					Alcohol and tobacco are always excluded on WhatsApp, and
					Meta bills the retailer directly for messages.
				</p>
			</InShort>

			<Section
				id="order-title"
				title="Everything a WhatsApp order needs."
				lead="Nothing a shopper has to learn: they text the way they already text."
			>
				<DetailGrid items={ORDER} />
			</Section>

			<Section
				id="chat-title"
				tone="mist"
				title="A WhatsApp grocery order, start to finish."
				lead="A Saudi shopper on a store that asks for the location first, with a voice note in the middle."
				center
			>
				<Conversation
					turns={CHAT}
					caption="A sample conversation written for this page to show the behaviour. It is not a transcript of a real shopper."
				/>
			</Section>

			<Article>
				<h2 id="how">How does WhatsApp ordering work, step by step?</h2>
				<p>
					WhatsApp commerce works when the shopper never has to learn
					anything new. They message your number the way they message
					a friend, and the assistant does the rest. Here is what
					happens in a typical order.
				</p>
				<ol>
					<li>
						<strong>The shopper writes, speaks or taps.</strong> A
						typed message, a voice note, a photo of a product, a tap
						on a card, a shared location or a reply to an earlier
						message. Each one is understood in the shopper's
						language, whether that is Gulf Arabic, Arabizi or
						English.
					</li>
					<li>
						<strong>The branch is set.</strong> If you choose to ask
						for the location first, nothing priced goes out until
						the shopper shares a pin or names their area. The
						nearest branch is picked, and the shopper chooses
						delivery or pickup. Every price and stock answer after
						that is the one that branch will deliver.
					</li>
					<li>
						<strong>Products arrive as cards.</strong> The assistant
						searches your live catalog, checks each result against
						what the shopper asked, and sends the matches as a
						WhatsApp list with prices and pictures. A broad request,
						such as a list for a family breakfast, comes back as one
						row per item.
					</li>
					<li>
						<strong>The basket fills.</strong> “The second one”,
						“two of those”, “something similar” and “anything
						cheaper” all work, because the assistant knows which
						products it just showed.
					</li>
					<li>
						<strong>The shopper confirms the checkout.</strong> The
						checkout opens as a form inside WhatsApp, with the
						basket, delivery details and payment option. The order
						is placed only when the shopper taps to confirm. A typed
						“yes” never places an order.
					</li>
					<li>
						<strong>Updates follow in the same chat.</strong>{" "}
						Confirmed, out for delivery, delivered: each change is
						sent as a WhatsApp message, with a link to an order
						tracking page.
					</li>
				</ol>
				<p>
					Replies are kept short: at most one message and one set of
					buttons or a list per turn, with the assistant's words
					folded into the interactive message when they fit.
				</p>
			</Article>

			<Section
				id="branch-title"
				tone="mist"
				title="The right branch, before any price."
			>
				<div className="space-y-20">
					<SplitRow
						title="Location first, where prices differ"
						text="A shared pin or a typed area picks the nearest branch. Until then nothing priced goes out, so every price and stock answer is the one that will be delivered."
						points={[
							"Delivery or pickup chosen in the chat",
							"Typed areas placed on the map, then the nearest branch",
						]}
						visual={<BranchVisual className="mb-8" />}
					/>
					<SplitRow
						reverse
						title="Every chat in one inbox"
						text="Conversations, orders and calls from your number land in your inbox as they happen. When a shopper asks for a person, your team replies in the same thread."
						points={[
							"Live conversations, with the products and basket the assistant showed",
							"Hand-overs flagged the moment a shopper asks for a person",
						]}
						visual={<InboxVisual className="mb-8" />}
					/>
				</div>
			</Section>

			<Article>
				<h2 id="checkout">
					How do checkout and payment work on WhatsApp?
				</h2>
				<p>
					When the shopper is ready, the assistant sends a checkout.
					It opens as a WhatsApp form with the basket, the delivery or
					pickup choice and the payment option, with a plain button to
					confirm as a fallback. Payment follows the options you
					choose: cash on delivery, card on delivery, or a payment
					link from your platform. The order then goes to your
					platform through its API, or to your team as an order ticket
					in the dashboard.
				</p>

				<h2 id="billing">Who pays for the WhatsApp messages?</h2>
				<p>
					You do, directly to Meta. Your WhatsApp Business number sits
					in your own business account on Meta's platform, and Meta
					bills its messages to you at Meta's prices. Hephon Commerce
					is not a WhatsApp Business Solution Provider reselling
					messages, and it does not add a margin to them. The
					dashboard shows an estimate of the WhatsApp cost next to
					your other usage, marked as billed by Meta.
				</p>

				<h2 id="restrictions">
					Why are alcohol and tobacco never shown on WhatsApp?
				</h2>
				<p>
					Meta's commerce policy does not allow them, so on every
					WhatsApp chat and call our server removes alcohol and
					tobacco from the catalog before the assistant sees it, even
					for a store with no other restrictions. A store that is set
					as Halal also hides pork, on every channel. Each product is
					judged by an AI model, not a word list, and a product that
					has not been checked yet stays hidden. A number starts
					answering shoppers only once every product in the catalog
					has been checked.
				</p>

				<h2 id="handover">
					What happens when a shopper wants a person?
				</h2>
				<p>
					Every WhatsApp conversation, voice note and call is in your
					team's live inbox, with the products the assistant showed
					and the basket it built. When a shopper asks for a person,
					the chat is flagged and anyone on your team can reply in the
					same WhatsApp thread. If your team works in a help desk, the
					hand-over can be sent there through an integration.
				</p>

				<h2 id="reach">How do shoppers find the number?</h2>
				<p>
					Share a wa.me link or print its QR code. You can make one
					for the whole store or one per branch: a shopper who scans a
					branch's code starts the chat in that branch, and the inbox
					shows where they came from. Shoppers who tap a
					click-to-WhatsApp ad reach the same assistant.
				</p>

				<h2 id="does-not">What it does not do</h2>
				<ul>
					<li>
						It never places an order without the shopper's tap on
						the checkout.
					</li>
					<li>
						It does not invent products, prices or delivery times.
						It offers what the shopper's branch has.
					</li>
					<li>
						It does not show alcohol or tobacco on WhatsApp, under
						any setting.
					</li>
				</ul>

				<h2 id="not-fit">When Hephon Commerce is not the right fit</h2>
				<ul>
					<li>
						You do not have, or cannot get, a WhatsApp Business
						number on Meta's official platform. It does not work on
						the WhatsApp Business app on a phone.
					</li>
					<li>
						You are looking for a WhatsApp Business Solution
						Provider to buy messages from, or a tool for campaigns
						and broadcasts.
					</li>
					<li>
						Most of what you sell is alcohol or tobacco, which
						WhatsApp does not allow.
					</li>
					<li>
						You have no online catalog with prices and stock per
						branch.
					</li>
				</ul>
			</Article>

			<Section
				id="setup-title"
				tone="mist"
				title="Set up and run for you."
				lead="We connect the number, keep it healthy and watch every message."
			>
				<DetailGrid items={SETUP} columns={4} />
			</Section>

			<Section id="steps-title" title="Live in three steps." center>
				<Steps items={STEPS} />
			</Section>

			<Faq items={FAQ} title="WhatsApp shopping, answered." />
			<RelatedPages links={RELATED} />
			<Closing />
		</>
	);
}
